'use client';
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-custom-classname */

import { ComparisonTableProps } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/types';
import { ApplyCtaLink } from '@hyphengroup/cms-components/components/ApplyCta';
import Button from '@hyphengroup/cms-components/components/Button';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { ProductCardOffer } from '@hyphengroup/cms-components/components/ProductCardOffer';
import { TheWrapper } from '@hyphengroup/cms-components/components/TheWrapper';
import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import {
  ComponentTypeEnum,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics/sendAnalytics';
import {
  formatAttributeItemValue,
  parseUrlAlias,
} from '@hyphengroup/cms-components/utils/helpers';
import classNames from 'classnames';
import { useRedirect } from 'hooks/useRedirect';
import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { sendProductAnalytics } from '../helpers/sendAnalytics';
import { TRANSLATIONS } from '../translations';
import { BaseAttribute } from '$/(cms)/_modules/cmsService/types';
import { generateButtonSubText } from '$/components/cms/helpers';
import { FeLink } from '$/components/FeLink';
import ArrowBottom from '$/components/icons/ArrowBottom';
import ArrowRightAlt from '$/components/icons/ArrowRightAlt';
import ArrowUp from '$/components/icons/ArrowUp';
import Close from '$/components/icons/Close';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';
// import './productComparisonTable.css';

const defaultActiveReward = -1;

type ProductComparisonTableProps = {
  products: ComparisonTableProps['products'];
  tableConfig: {
    attributes: ComparisonTableProps['tableConfig']['attributes'];
    titleReq: ComparisonTableProps['tableConfig']['titleReq'];
    visible_mobile: ComparisonTableProps['tableConfig']['visible_mobile'];
  };
};

export const ProductComparisonTable: React.FC<ProductComparisonTableProps> = (
  props,
) => {
  const {
    products,
    tableConfig: { attributes, titleReq, visible_mobile },
  } = props;
  const context = useClientContext();
  const { brand, lang } = context;
  const redirect = useRedirect();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const attribute_list = useMemo(
    () =>
      attributes?.reduce<Record<string, string>>((prev, current) => {
        if (!current?.attribute?.name || !current?.label) return prev;
        return {
          ...prev,
          [current.attribute.name]: current.label,
        };
      }, {}) ?? {},
    [attributes],
  );

  const attribute_list_additional = useMemo(
    () =>
      attributes?.reduce<Record<string, string>>((prev, current) => {
        if (!current?.attribute?.name || !current?.additional_information)
          return prev;
        return {
          ...prev,
          [current.attribute.name]: current.additional_information,
        };
      }, {}) ?? {},
    [attributes],
  );

  const [activeReward, setActiveReward] = useState(defaultActiveReward);
  const scrollable = useRef<HTMLDivElement>(null);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

  const scrollableRef = useMemo(() => scrollable.current, [scrollable]);

  useEffect(() => {
    const updateScrolledToEnd = () => {
      const container = scrollableRef;
      if (!container) {
        return;
      }
      setIsScrolledToEnd(
        container.scrollLeft + container.clientWidth >= container.scrollWidth,
      );
    };
    updateScrolledToEnd();
  }, [scrollableRef]);

  const handleOpenRewards = (rowNumber: number) => {
    setActiveReward((oldValue) =>
      oldValue === rowNumber ? defaultActiveReward : rowNumber,
    );
  };

  const handleCloseRewards = () => {
    setActiveReward(defaultActiveReward);
  };

  // We can keep the header and cell components only in FE side,
  // on the parent page, to be able to customize other tables easily
  const header = useMemo<string[]>(
    () => [
      staticText.products,
      ...Object.values(attribute_list),
      staticText.offers_rewards,
      staticText.apply,
    ],
    [staticText, attribute_list],
  );

  const headerLength = useMemo(() => header.length, [header]);

  const getTooltipContent = (
    attributeTitle: string,
    attributes: BaseAttribute[],
    key: string,
  ) => {
    const additionalInfo = getAdditionalInformation(attributes, key);
    if (additionalInfo) {
      return `${attributeTitle}<br><br>${additionalInfo}`;
    }
    return attributeTitle;
  };

  const shouldTruncate = (text: string, maxLength: number = 15) => {
    return text.length > maxLength;
  };

  const shouldShowTooltip = (
    attributeTitle: string,
    attributes: BaseAttribute[],
    key: string,
    maxLength = 15,
  ) => {
    const additionalInfo = getAdditionalInformation(attributes, key);
    return shouldTruncate(attributeTitle, maxLength) || additionalInfo;
  };

  const condenseText = (text: string) => {
    if (!text) return text;

    const patterns = [
      {
        pattern:
          /(\d+(\.\d+)?)\s*-\s*(\d+(\.\d+)?)\s+mile(?:s)?\s+per\s+dollar/gi,
        replacement: '$1-$3 miles/$',
      },
      {
        pattern:
          /(\d+(\.\d+)?)\s*-\s*(\d+(\.\d+)?)\s+point(?:s)?\s+per\s+dollar/gi,
        replacement: '$1-$3 points/$',
      },
      {
        pattern: /(\d+(\.\d+)?)\s*-\s*(\d+(\.\d+)?)%\s+cashback/gi,
        replacement: '$1-$3% cashback',
      },
      {
        pattern: /(\d+)\s*-\s*(\d+)\s+point(?:s)?\s+per\s+mile/gi,
        replacement: '$1-$2 points/mile',
      },
      {
        pattern: /(\d+)\s*-\s*(\d+)\s+visit(?:s)?\s+per\s+year/gi,
        replacement: '$1-$2 visits/yr',
      },
      {
        pattern: /(\d+(\.\d+)?)\s+point(?:s)?\s+per\s+dollar/gi,
        replacement: '$1 point/$',
      },
      {
        pattern: /(\d+(\.\d+)?)\s+mile(?:s)?\s+per\s+dollar/gi,
        replacement: '$1 miles/$',
      },
      {
        pattern: /(\d+(\.\d+)?)\s+point(?:s)?\s+per\s+mile/gi,
        replacement: '$1 point/mile',
      },
      {
        pattern: /(\d+(\.\d+)?)\s+visit(?:s)?\s+per\s+year/gi,
        replacement: '$1 visits/yr',
      },
    ];

    let condensedText = text;
    patterns.forEach(({ pattern, replacement }) => {
      condensedText = condensedText.replace(pattern, replacement);
    });

    return condensedText;
  };

  const getAttributeName = (attribute: BaseAttribute): string => {
    switch (attribute?.__typename) {
      case 'ComponentAttributesAttrRichTextEditor':
      case 'ComponentAttributesAttrNumberRange':
      case 'ComponentAttributesAttrNumber':
      case 'ComponentAttributesAttrBoolean':
      case 'ComponentAttributesAttrPlainText':
      case 'ComponentAttributesAttrKeyValue':
      case 'ComponentAttributesAttrJson': {
        return attribute!.name?.name ?? '';
      }
    }
    return attribute?.__typename ?? '';
  };

  const findByTypeAndConcatenate = (
    items: BaseAttribute[] | null | undefined,
    name: string,
  ) => {
    if (!items) return staticText.n_a;
    const item = items.find((item) => getAttributeName(item) === name);
    if (item) {
      const value = formatAttributeItemValue(item as unknown as BaseAttribute);
      return value ?? staticText.n_a;
    }
    return staticText.n_a;
  };

  const findByTypeAndConcatenateCondensed = (
    attributes: BaseAttribute[],
    key: string,
  ) => {
    const originalText = findByTypeAndConcatenate(attributes, key);
    return condenseText(originalText);
  };

  const getAdditionalInformation = (items: BaseAttribute[], name: string) => {
    const item = items.find((item) => getAttributeName(item) === name);
    if (item) {
      switch (item.__typename) {
        case 'ComponentAttributesAttrNumberRange':
        case 'ComponentAttributesAttrNumber': {
          return item.additional_information ?? null;
        }
      }
    }
    return null;
  };

  const openProduct = (url?: string | null) => {
    // The stored url_alias may carry the #usePDP marker (CMS-1924) — strip it
    // before navigating so it never reaches the address bar.
    const pageUrl = parseUrlAlias(url ?? undefined).url;
    if (pageUrl && typeof window !== 'undefined') {
      redirect(pageUrl);
    }
  };

  const wrapperStyleProperties = {
    '--header-length': headerLength,
  } as CSSProperties;

  return (
    <TheWrapper
      type="full"
      title={titleReq}
      extraClass={visible_mobile ? '' : 'hidden md:block'}
    >
      <div
        className={classNames(
          {
            'mr-[-1.5rem]': isScrolledToEnd,
            'mr-0': isScrolledToEnd,
          },
          'mb-6',
          'mt-6',
          'card-radius',
          'select-none',
          'lg:mr-0',
          'relative',
          'product-comparison-table',
        )}
      >
        <div
          style={wrapperStyleProperties}
          ref={scrollable}
          className="table-container"
        >
          {/* Desktop View */}
          <table className="hidden w-full bg-white lg:table">
            <thead className="contents text-left bg-gray-100">
              <tr className="contents">
                {header.map((tab, index) => (
                  <th
                    key={tab}
                    className={`flex items-center p-4 bg-gray-100 alpha-overline xl:p-6 ${index >= header.length - 2 ? 'hidden lg:flex' : ''} ${index === header.length - 2 ? 'offers-rewards' : ''}`}
                  >
                    {tab}
                    {attribute_list_additional &&
                      attribute_list_additional[tab] && (
                        <Tooltip
                          content={attribute_list_additional[tab]}
                          holderClasses={['ml-1']}
                        />
                      )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="contents">
              {!products?.filter(Boolean).length && (
                <tr className="contents">
                  <td>{staticText.n_a}</td>
                </tr>
              )}
              {products?.filter(Boolean).map((product, rowIndex) => (
                <React.Fragment key={product?.uid}>
                  {/*TODO: add vertical logic*/}
                  <tr className="table-line contents lg:hover:bg-gray-50">
                    {/* Image & name START */}
                    <td className="flex items-center border-t border-gray-300 p-4 bg-white xl:p-6">
                      <div className="flex flex-col sm:gap-4">
                        <div className="flex w-28 items-center justify-center">
                          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/anchor-is-valid */}
                          <FeLink
                            href="#"
                            onClick={() => openProduct(product?.url_alias)}
                          >
                            <CmsImage
                              unoptimized
                              src={product?.product_image ?? ''}
                              alt={product?.product_name ?? 'Product'}
                              height={75}
                              width={112}
                              className="max-h-[75px] w-auto cursor-pointer object-contain"
                            />
                          </FeLink>
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <FeLink
                          href={parseUrlAlias(product?.url_alias).url || '#'}
                          className="xs:max-w-50 !text-gray-900 alpha-body-2 hover:underline hover:!text-gray-800 sm:max-w-75 lg:max-w-full"
                        >
                          {product?.product_name}
                        </FeLink>
                      </div>
                    </td>
                    {/* Image & name END */}

                    {/* Attributes START */}
                    {Object.keys(attribute_list).map((key) => (
                      <td
                        key={key}
                        className="flex items-center border-t border-gray-300 p-4 bg-white xl:p-6"
                      >
                        <div className="col flex items-center gap-1">
                          <p className="whitespace-nowrap alpha-body-1 lg:whitespace-normal">
                            {findByTypeAndConcatenate(
                              product?.attributes ?? [],
                              key,
                            )}
                          </p>
                          {getAdditionalInformation(
                            product?.attributes ?? [],
                            key,
                          ) && (
                            <Tooltip
                              content={
                                getAdditionalInformation(
                                  product?.attributes ?? [],
                                  key,
                                ) ?? ''
                              }
                              // popupStyle={{ maxWidth: '300px' }}
                            />
                          )}
                        </div>
                      </td>
                    ))}
                    {/* Attributes END */}

                    {/* Rewards & button START */}
                    <td className="offers-rewards hidden items-center border-t border-gray-300 p-4 bg-white lg:inline-flex xl:p-6">
                      {(product?.exclusive_offers?.length ?? 0) > 0 ||
                      (product?.welcome_offers?.length ?? 0) > 0 ? (
                        <>
                          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                          <p
                            onClick={() => handleOpenRewards(rowIndex)}
                            className="hidden cursor-pointer text-nowrap !font-semibold underline !text-[--buttons-primary] alpha-body-2 lg:inline-block"
                          >
                            {staticText.eligible_rewards} (
                            {(product?.exclusive_offers?.length ?? 0) +
                              (product?.welcome_offers?.length ?? 0)}
                            )
                          </p>
                          <div className="inline-block alpha-body-2 lg:hidden">
                            <Button
                              onClick={() => handleOpenRewards(rowIndex)}
                              title={`${staticText.eligible_rewards} (${(product?.exclusive_offers?.length ?? 0) + (product?.welcome_offers?.length ?? 0)})`}
                              color="text-[--buttons-primary]"
                            >{`${staticText.eligible_rewards} (${(product?.exclusive_offers?.length ?? 0) + (product?.welcome_offers?.length ?? 0)})`}</Button>
                          </div>
                        </>
                      ) : (
                        <div>{staticText.n_a}</div>
                      )}
                    </td>
                    <td className="hidden items-center border-t border-gray-300 p-4 bg-white lg:flex xl:p-6">
                      {product?.call_to_action?.links?.[0]?.redirected_url && (
                        <div className="flex flex-auto flex-col gap-2">
                          <ApplyCtaLink
                            product={product}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              title={product?.call_to_action?.links?.[0]?.label}
                              endIcon={<ArrowRightAlt className="size-5" />}
                              // classes="!w-full !min-w-[215px]"
                              className="w-full sm:w-54"
                              styleType="cta"
                              onClick={(e) => {
                                e.stopPropagation();
                                sessionAttributionTracker.recordApplyClick();
                                sendProductAnalytics({
                                  action: UserActionEnum.CLICK,
                                  componentTitle: `${titleReq}_${product?.call_to_action?.links?.[0]?.label}`,
                                  componentType: ComponentTypeEnum.TABLE,
                                  event: 'product_click_apply',
                                  index: rowIndex.toString(),
                                  product: product,
                                });
                              }}
                            >
                              {product?.call_to_action?.links?.[0]?.label}
                            </Button>
                          </ApplyCtaLink>
                          <p className="text-center font-medium text-gray-700 alpha-body-2 leading-none">
                            {generateButtonSubText({
                              btnLabel:
                                product?.call_to_action?.links?.[0]?.label,
                              appName: brand,
                              lang: lang,
                              providerName:
                                product?.provider?.provider_name ?? '',
                            })}
                          </p>
                        </div>
                      )}
                    </td>
                    <td
                      colSpan={headerLength}
                      className="flex p-4 bg-white lg:hidden xl:p-6"
                    >
                      <div className="flex flex-row-reverse gap-2">
                        {((product?.exclusive_offers?.length ?? 0) > 0 ||
                          (product?.welcome_offers?.length ?? 0) > 0) && (
                          <>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <p
                              onClick={() => handleOpenRewards(rowIndex)}
                              className="hidden cursor-pointer text-nowrap !font-semibold underline !text-[--buttons-primary] alpha-body-2 lg:inline-block"
                            >
                              {staticText.eligible_rewards} (
                              {(product?.exclusive_offers?.length ?? 0) +
                                (product?.welcome_offers?.length ?? 0)}
                              )
                            </p>
                            <div className="inline-block alpha-body-2 lg:hidden">
                              <Button
                                onClick={() => handleOpenRewards(rowIndex)}
                                title={`${staticText.eligible_rewards} (${(product?.exclusive_offers?.length ?? 0) + (product?.welcome_offers?.length ?? 0)})`}
                                className="bg-white"
                                endIcon={<ArrowRightAlt className="size-5" />}
                              >
                                {`${staticText.eligible_rewards} (${(product?.exclusive_offers?.length ?? 0) + (product?.welcome_offers?.length ?? 0)})`}
                              </Button>
                            </div>
                          </>
                        )}
                        {product?.call_to_action?.links?.[0]
                          ?.redirected_url && (
                          <div className="flex flex-col gap-2">
                            <ApplyCtaLink
                              product={product}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button
                                title={
                                  product?.call_to_action?.links?.[0]?.label
                                }
                                endIcon={<ArrowRightAlt className="size-5" />}
                                className="w-39 sm:w-41"
                                styleType="cta"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sessionAttributionTracker.recordApplyClick();
                                  sendProductAnalytics({
                                    action: UserActionEnum.CLICK,
                                    componentTitle: `${titleReq}_${product?.call_to_action?.links?.[0]?.label}`,
                                    componentType: ComponentTypeEnum.TABLE,
                                    event: 'product_click_apply',
                                    index: rowIndex.toString(),
                                    product: product,
                                  });
                                }}
                              >
                                {product?.call_to_action?.links?.[0]?.label}
                              </Button>
                            </ApplyCtaLink>
                            <p className="xs:max-w-[155px] text-center font-medium text-gray-700 alpha-body-2 leading-none sm:max-w-auto">
                              {generateButtonSubText({
                                btnLabel:
                                  product?.call_to_action?.links?.[0]?.label,
                                appName: brand,
                                lang: lang,
                                providerName:
                                  product?.provider?.provider_name ?? '',
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Rewards & button END */}
                  </tr>
                  {/* Common table row END */}

                  {/* Expandable row START */}
                  {((product?.exclusive_offers?.length ?? 0) > 0 ||
                    (product?.welcome_offers?.length ?? 0) > 0) &&
                    activeReward === rowIndex && (
                      <tr className="contents">
                        <td
                          colSpan={headerLength}
                          className="relative flex p-4 bg-[--highlights-blue-light] xl:p-6"
                        >
                          <div className="flex w-full flex-col">
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                            <div
                              className="flex cursor-pointer justify-between"
                              onClick={() => handleCloseRewards()}
                            >
                              <p className="mb-6 text-[--buttons-primary] alpha-subheading-2">
                                {staticText.eligible_rewards} (
                                {(product?.exclusive_offers?.length ?? 0) +
                                  (product?.welcome_offers?.length ?? 0)}
                                )
                              </p>
                              {/*<i dangerouslySetInnerHTML={{ __html: icons.arrow }}></i>*/}
                              {activeReward === rowIndex ? (
                                <ArrowUp className="size-[--shapes-icon-size-lg]" />
                              ) : (
                                <ArrowBottom className="size-[--shapes-icon-size-lg]" />
                              )}
                            </div>

                            {/* Rewards section START */}
                            <div className="flex flex-col gap-4 alpha-body-2">
                              {product?.exclusive_offers &&
                                product?.exclusive_offers
                                  ?.filter(Boolean)
                                  ?.map((offer) => (
                                    <ProductCardOffer
                                      key={offer.uid}
                                      {...offer}
                                    />
                                  ))}
                              {product?.welcome_offers &&
                                product?.welcome_offers
                                  ?.filter(Boolean)
                                  ?.map((offer) => (
                                    <ProductCardOffer
                                      key={offer.uid}
                                      {...offer}
                                    />
                                  ))}
                            </div>
                            {/* Rewards section END */}
                          </div>
                        </td>
                      </tr>
                    )}
                  {/* Expandable row END */}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {/* END Desktop View */}

          {/* Mobile View */}
          <div className="mobile-table block border-b shadow lg:hidden">
            {!products?.filter(Boolean).length && <div>{staticText.n_a}</div>}
            {products?.filter(Boolean).map((product, rowIndex) => (
              <div
                key={product?.uid}
                className={`mobile-card ${rowIndex !== Object.keys(products).length - 1 ? 'border-b' : ''}`}
              >
                {/* Image & Title */}
                <div className="mobile-card-header">
                  {/*TODO: add vetical logic*/}
                  <div className="flex items-center gap-2">
                    <div className="shrink-0">
                      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/anchor-is-valid */}
                      <FeLink
                        href="#"
                        onClick={() => openProduct(product?.url_alias)}
                      >
                        <CmsImage
                          unoptimized
                          src={product?.product_image ?? ''}
                          alt={product?.product_name ?? 'Product'}
                          height={80}
                          width={100}
                          className="h-full max-h-20 w-25 cursor-pointer object-contain"
                        />
                      </FeLink>
                    </div>
                    <div className="grow">
                      <FeLink
                        href={parseUrlAlias(product?.url_alias).url || '#'}
                        className="product-title text-gray-900 text-lg hover:underline hover:text-gray-800"
                      >
                        {product?.product_name}
                      </FeLink>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="mobile-card-buttons">
                  <div className="xs-sm:justify-start flex justify-between gap-2">
                    <span className="flex-1">
                      <Button
                        title="Read More"
                        className="w-full max-w-full"
                        variant="outline"
                        styleType="neutral"
                        onClick={() => openProduct(product?.url_alias)}
                      >
                        Read More
                      </Button>
                    </span>
                    {product?.call_to_action?.links?.[0]?.redirected_url && (
                      <ApplyCtaLink
                        product={product}
                        className="flex-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          title={product?.call_to_action?.links?.[0]?.label}
                          className="w-full max-w-full"
                          styleType="cta"
                          endIcon={<ArrowRightAlt className="size-5" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            sessionAttributionTracker.recordApplyClick();
                            sendProductAnalytics({
                              action: UserActionEnum.CLICK,
                              componentTitle: `${titleReq}_${product?.call_to_action?.links?.[0]?.label}`,
                              componentType: ComponentTypeEnum.TABLE,
                              event: 'product_click_apply',
                              index: rowIndex.toString(),
                              product: product,
                            });
                          }}
                        >
                          {product?.call_to_action?.links?.[0]?.label}
                        </Button>
                      </ApplyCtaLink>
                    )}
                  </div>
                </div>

                <div className="mobile-card-attributes">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(80px,1fr))] gap-x-4">
                    {Object.keys(attribute_list)
                      .filter((key) => key !== 'MINIMUM_ANNUAL_INCOME')
                      .map((key) => (
                        <div
                          key={key}
                          className="short-attribute flex flex-col lg:col-span-1"
                        >
                          <div className="flex items-center justify-start">
                            <span className="attribute-title truncate whitespace-nowrap text-sm">
                              {attribute_list[key]}
                            </span>
                            {shouldShowTooltip(
                              attribute_list[key],
                              product?.attributes ?? [],
                              key,
                            ) && (
                              <Tooltip
                                content={getTooltipContent(
                                  attribute_list[key],
                                  product?.attributes ?? [],
                                  key,
                                )}
                              />
                              // TODO: use TooltipText when be available
                              // <TooltipText
                              //   tooltipText={getTooltipContent(attribute_list[key], product.attributes, key)}
                              //   mobileIcon={true}
                              //   className="ml-1 shrink-0"
                              // />
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-semibold lg:truncate">
                              {findByTypeAndConcatenateCondensed(
                                product?.attributes ?? [],
                                key,
                              )}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Rewards Link */}
                {((product?.exclusive_offers?.length ?? 0) > 0 ||
                  (product?.welcome_offers?.length ?? 0) > 0) && (
                  <div className="mobile-card-rewards mb-4">
                    <div className="flex items-center justify-between">
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenRewards(rowIndex);
                        }}
                        className="cursor-pointer font-semibold underline decoration-1 text-[--buttons-primary]"
                      >
                        {staticText.eligible_rewards} (
                        {(product?.exclusive_offers?.length ?? 0) +
                          (product?.welcome_offers?.length ?? 0)}
                        )
                      </span>
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                      <i
                        onClick={() =>
                          activeReward === rowIndex
                            ? handleCloseRewards()
                            : handleOpenRewards(rowIndex)
                        }
                        className={`cursor-pointer ${activeReward === rowIndex ? 'mr-[-1.5px]' : 'mr-[-8.5px]'}`}
                      >
                        {activeReward === rowIndex ? (
                          <Close className="size-[--shapes-icon-size-lg]" />
                        ) : (
                          <ArrowUp className="size-[--shapes-icon-size-lg] rotate-90" />
                        )}
                      </i>
                    </div>
                  </div>
                )}

                {/* Mobile Expandable Rewards */}
                {((product?.exclusive_offers?.length ?? 0) > 0 ||
                  (product?.welcome_offers?.length ?? 0) > 0) &&
                  activeReward === rowIndex && (
                    <div className="expanded-card-rewards -mx-4 p-4 bg-[--highlights-blue-light] xl:p-6">
                      <div className="flex w-full flex-col">
                        <div className="flex flex-col gap-4 alpha-body-2">
                          {product?.exclusive_offers &&
                            product?.exclusive_offers
                              ?.filter(Boolean)
                              ?.map((offer) => (
                                <ProductCardOffer key={offer.uid} {...offer} />
                              ))}
                          {product?.welcome_offers &&
                            product?.welcome_offers
                              ?.filter(Boolean)
                              ?.map((offer) => (
                                <ProductCardOffer key={offer.uid} {...offer} />
                              ))}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
          {/* END Mobile View */}
        </div>
      </div>
    </TheWrapper>
  );
};
